import { useEffect, useRef, useState } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Sparkles, CheckCircle2, AlertTriangle, RefreshCw, Copy, Plus, Trash2, Eye, EyeOff, Zap } from "lucide-react";

export default function FathomSettings() {
  const [settings, setSettings] = useState(null);
  const [reps, setReps] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [events, setEvents] = useState([]);
  const [unlinkedQueue, setUnlinkedQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [days, setDays] = useState(30);
  const [newFathomEmail, setNewFathomEmail] = useState("");
  const [newHqUserId, setNewHqUserId] = useState("");
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [webhookSecretInput, setWebhookSecretInput] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [provisioning, setProvisioning] = useState(false);
  const [savingAi, setSavingAi] = useState(false);
  const [relinking, setRelinking] = useState(false);
  const [roster, setRoster] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);

  // Webhook URL is hardcoded to the production domain (hq.therenderpros.com)
  // so the URL displayed here is the one Fathom should always POST to,
  // regardless of whether HQ is currently being viewed on preview or prod.
  const webhookUrl = "https://hq.therenderpros.com/api/fathom/webhook";

  const load = async () => {
    setLoading(true);
    try {
      const [s, m, u, e, q] = await Promise.all([
        apiClient.get("/fathom/settings"),
        apiClient.get("/fathom/rep-mappings"),
        apiClient.get("/users?role=Sales"),
        apiClient.get("/fathom/event-log?limit=50"),
        apiClient.get("/fathom/unlinked-queue?limit=20"),
      ]);
      setSettings(s.data);
      setMappings(m.data.items || []);
      setReps(Array.isArray(u.data) ? u.data : (u.data?.items || []));
      setEvents(e.data.items || []);
      setUnlinkedQueue(q.data.items || []);
    } catch (err) {
      toast.error("Failed to load Fathom settings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); loadRoster(); }, []);

  const loadRoster = async () => {
    setRosterLoading(true);
    try {
      const r = await apiClient.get("/fathom/org-users");
      setRoster(r.data);
    } catch (e) {
      setRoster({ items: [], error: e?.response?.data?.detail || "Could not load the Fathom roster" });
    } finally { setRosterLoading(false); }
  };

  const provisionWebhook = async () => {
    setProvisioning(true);
    try {
      const r = await apiClient.post("/fathom/provision-webhook", { destination_url: webhookUrl });
      if (r.data?.ok) {
        toast.success(`Webhook created in Fathom (covers: ${(r.data.triggered_for || []).join(", ")}) — signing secret saved automatically`);
        await load();
      } else {
        toast.error(r.data?.error || "Provisioning failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provisioning failed");
    } finally { setProvisioning(false); }
  };

  const toggleOwnAi = async (enable) => {
    setSavingAi(true);
    try {
      await apiClient.post("/fathom/settings", { ai_extraction_enabled: enable });
      toast.success(enable
        ? "HQ analysis on — new calls get risk flags and cross-call roll-ups"
        : "HQ analysis off — calls sync with Fathom's own summaries and action items");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update setting");
    } finally { setSavingAi(false); }
  };

  const pollTimerRef = useRef(null);

  const pollBackfill = () => {
    if (pollTimerRef.current) return; // already polling
    const timer = setInterval(async () => {
      try {
        const s = await apiClient.get("/fathom/settings");
        setSettings(s.data);
        if (s.data.last_sync_status !== "running") {
          clearInterval(timer);
          pollTimerRef.current = null;
          setBackfilling(false);
          const p = s.data.sync_progress;
          if (s.data.last_sync_status === "error") {
            toast.error(s.data.last_error || "Backfill failed");
          } else {
            toast.success(`Backfill finished — ${p ? `${p.done} processed, ${p.errors} errors` : "done"}`);
          }
          await load();
        }
      } catch (e) { /* transient poll failure — keep polling */ }
    }, 8000);
    pollTimerRef.current = timer;
  };

  // Resume polling if the page loads while a backfill is running
  useEffect(() => {
    if (settings?.last_sync_status === "running") {
      setBackfilling(true);
      pollBackfill();
    }
    // eslint-disable-next-line
  }, [settings?.last_sync_status]);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await apiClient.post("/fathom/backfill", { days });
      if (r.data?.started) {
        toast.success(`Backfill started — processing ${r.data.total} meetings in the background. AI extraction takes a while; progress updates below.`);
        await load();
        pollBackfill(); // keeps backfilling=true until the job finishes
        return;
      }
      if (r.data?.error) {
        toast.error(r.data.error);
      }
      setBackfilling(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
      setBackfilling(false);
    }
  };

  const runRelink = async () => {
    setRelinking(true);
    try {
      const r = await apiClient.post("/fathom/relink-auto");
      const d = r.data || {};
      toast.success(`Re-linking done — ${d.checked} calls checked, ${d.relinked} re-linked, ${d.unlinked} moved to the unlinked queue.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-linking failed");
    } finally { setRelinking(false); }
  };

  const createMapping = async () => {
    if (!newFathomEmail || !newHqUserId) return toast.error("Email and HQ user required");
    try {
      await apiClient.post("/fathom/rep-mappings", { fathom_email: newFathomEmail, hq_user_id: newHqUserId });
      setNewFathomEmail("");
      setNewHqUserId("");
      await load();
      toast.success("Mapping saved");
    } catch (e) { toast.error("Failed to save"); }
  };

  const deleteMapping = async (id) => {
    if (!window.confirm("Remove this mapping?")) return;
    try { await apiClient.delete(`/fathom/rep-mappings/${id}`); await load(); toast.success("Removed"); }
    catch { toast.error("Delete failed"); }
  };

  const copyWebhookUrl = async () => {
    try { await navigator.clipboard.writeText(webhookUrl); toast.success("Webhook URL copied"); }
    catch { toast.error("Copy failed"); }
  };

  const saveSettings = async () => {
    if (!apiKeyInput && !webhookSecretInput) return toast.error("Enter an API key or webhook secret to save");
    setSaving(true);
    try {
      const payload = {};
      if (apiKeyInput) payload.api_key = apiKeyInput;
      if (webhookSecretInput) payload.webhook_secret = webhookSecretInput;
      await apiClient.post("/fathom/settings", payload);
      toast.success("Settings saved");
      setApiKeyInput("");
      setWebhookSecretInput("");
      setTestResult(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await apiClient.post("/fathom/test-connection");
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Request failed" });
    } finally { setTesting(false); }
  };

  if (loading || !settings) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="fathom-settings-page">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">
        <Sparkles className="w-3.5 h-3.5" /> Fathom Intelligence · Admin
      </div>
      <h1 className="text-3xl font-light text-slate-900 tracking-tight mb-1">Integration Settings</h1>
      <p className="text-sm text-slate-500 mb-6">Configure the webhook, map Fathom users to HQ reps, and review the unlinked queue.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total calls" value={settings.total_calls} />
        <StatCard label="Unlinked / suggested" value={settings.unlinked_count} tone={settings.unlinked_count > 0 ? "amber" : ""} />
        <StatCard label="Unassigned rep" value={settings.unassigned_rep_count} tone={settings.unassigned_rep_count > 0 ? "amber" : ""} />
        <StatCard label="Last synced" value={settings.last_synced_at ? new Date(settings.last_synced_at).toLocaleString() : "Never"} small />
      </div>

      <Tabs defaultValue="webhook">
        <TabsList>
          <TabsTrigger value="webhook" data-testid="tab-webhook">Webhook & Sync</TabsTrigger>
          <TabsTrigger value="mappings" data-testid="tab-mappings">Rep Mapping</TabsTrigger>
          <TabsTrigger value="queue" data-testid="tab-queue">Unlinked Queue {unlinkedQueue.length > 0 && <Badge variant="outline" className="ml-1.5 text-[10px] bg-amber-50 text-amber-700 border-amber-200">{unlinkedQueue.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Event Log</TabsTrigger>
        </TabsList>

        <TabsContent value="webhook" className="mt-4 space-y-4">
          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">Webhook destination URL</div>
            <div className="flex items-center gap-2">
              <Input readOnly value={webhookUrl} className="font-mono text-xs" data-testid="webhook-url-input" />
              <Button size="sm" variant="outline" onClick={copyWebhookUrl} data-testid="copy-webhook-url"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy</Button>
            </div>
            <div className="mt-4 p-3 rounded bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-2">
              <div className="font-semibold text-slate-800">Recommended: provision automatically</div>
              <p>
                One click creates the webhook in Fathom via the API — set to fire for <strong>your recordings and all team recordings</strong>,
                with summary, transcript, action items, and CRM matches included. The signing secret is saved into HQ automatically.
              </p>
              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" onClick={provisionWebhook} disabled={provisioning || !settings.api_key_configured} data-testid="provision-webhook-btn">
                  <Zap className="w-3.5 h-3.5 mr-1.5" /> {provisioning ? "Provisioning…" : (settings.provisioned_webhook_id ? "Re-provision webhook" : "Provision webhook")}
                </Button>
                {settings.provisioned_webhook_id && (
                  <span className="flex items-center gap-1.5 text-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Provisioned {settings.provisioned_webhook_at && new Date(settings.provisioned_webhook_at).toLocaleString()}
                    {settings.provisioned_webhook_triggered_for && ` · covers ${settings.provisioned_webhook_triggered_for.join(", ")}`}
                  </span>
                )}
              </div>
              {settings.provisioned_webhook_id && (
                <p className="text-amber-700">
                  If you created a webhook manually in Fathom before, delete it there (Settings → API Access → Webhooks) — its
                  deliveries use an old secret and will be rejected.
                </p>
              )}
              <div className="font-semibold text-slate-800 pt-1">Manual alternative:</div>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Open Fathom → <strong>Settings → API Access → Webhooks → Add Webhook</strong></li>
                <li>Paste the URL above as the destination and select <em>all team recordings</em>.</li>
                <li>Enable payload inclusions: <strong>summary, transcript, action items</strong>.</li>
                <li>Copy the signing secret (starts with <code className="px-1 py-0.5 rounded bg-white border">whsec_</code>) into the field below and save. HQ verifies every delivery's signature automatically.</li>
              </ol>
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5">
                {settings.api_key_configured ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                Fathom API key: <span className={settings.api_key_configured ? "text-emerald-700" : "text-amber-700"}>{settings.api_key_configured ? "configured" : "missing"}</span>
              </span>
              <span className="flex items-center gap-1.5">
                {settings.webhook_secret_configured ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                Webhook secret: <span className={settings.webhook_secret_configured ? "text-emerald-700" : "text-amber-700"}>{settings.webhook_secret_configured ? "configured" : "missing"}</span>
              </span>
            </div>
          </Card>

          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">API credentials</div>
            <p className="text-sm text-slate-600 mb-3">
              Enter your Fathom API key to enable backfill and direct API access.
              Get it from <strong>Fathom → Settings → API</strong>. Stored encrypted in HQ's database — you never need SSH to configure this.
            </p>
            <div className="space-y-3">
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1">Fathom API Key</div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showApiKey ? "text" : "password"}
                      placeholder={settings.api_key_configured ? "••••••••  (already configured — paste new value to replace)" : "Paste your Fathom API key…"}
                      value={apiKeyInput}
                      onChange={(e) => setApiKeyInput(e.target.value)}
                      className="pr-9 font-mono text-xs"
                      data-testid="api-key-input"
                    />
                    <button
                      type="button"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowApiKey(v => !v)}
                    >
                      {showApiKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1">Webhook Signing Secret</div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showSecret ? "text" : "password"}
                      placeholder={settings.webhook_secret_configured ? "••••••••  (already configured — paste new value to replace)" : "Paste webhook signing secret from Fathom…"}
                      value={webhookSecretInput}
                      onChange={(e) => setWebhookSecretInput(e.target.value)}
                      className="pr-9 font-mono text-xs"
                      data-testid="webhook-secret-input"
                    />
                    <button
                      type="button"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowSecret(v => !v)}
                    >
                      {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button onClick={saveSettings} disabled={saving || (!apiKeyInput && !webhookSecretInput)} data-testid="save-credentials-btn">
                  {saving ? "Saving…" : "Save credentials"}
                </Button>
                <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="test-connection-btn">
                  <Zap className="w-3.5 h-3.5 mr-1.5" />{testing ? "Testing…" : "Test connection"}
                </Button>
                {testResult && (
                  <span className={`flex items-center gap-1.5 text-xs font-medium ${testResult.ok ? "text-emerald-700" : "text-rose-700"}`}>
                    {testResult.ok
                      ? <><CheckCircle2 className="w-3.5 h-3.5" /> {testResult.message || "Connected"}</>
                      : <><AlertTriangle className="w-3.5 h-3.5" /> {testResult.error}</>
                    }
                  </span>
                )}
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">AI analysis</div>
            <p className="text-sm text-slate-600 mb-3">
              Fathom already writes a summary and action items for every call, and HQ always stores those — free.
              On top of that, HQ can run its own analysis to extract things Fathom doesn't provide:
              risk flags, commitments made, pricing mentions, and cross-call project roll-ups. That part uses
              your Anthropic credits, roughly a few cents per call.
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant={settings.ai_extraction_enabled === false ? "default" : "outline"}
                size="sm"
                onClick={() => toggleOwnAi(settings.ai_extraction_enabled === false)}
                disabled={savingAi}
                data-testid="toggle-own-ai"
              >
                {savingAi ? "Saving…" : settings.ai_extraction_enabled === false ? "Turn HQ analysis on" : "Turn HQ analysis off"}
              </Button>
              <span className="text-xs text-slate-500">
                Currently <strong className={settings.ai_extraction_enabled === false ? "text-slate-700" : "text-emerald-700"}>
                  {settings.ai_extraction_enabled === false ? "off — Fathom's own summaries only" : "on"}
                </strong>
              </span>
            </div>
            {settings.ai_extraction_halted_reason && settings.ai_extraction_enabled !== false && (
              <div className="mt-3 p-2 text-xs text-amber-800 bg-amber-50 rounded border border-amber-200">
                HQ analysis paused automatically: {settings.ai_extraction_halted_reason.slice(0, 200)}
                <br />Calls still sync with Fathom's own summaries and action items. Fix the account, then run a backfill to retry.
              </div>
            )}
          </Card>

          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">Historical backfill</div>
            <p className="text-sm text-slate-600 mb-3">Pull existing Fathom recordings from the past N days into HQ. Idempotent — already-synced calls are updated, not duplicated.</p>
            <div className="flex items-center gap-2">
              <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(parseInt(e.target.value) || 30)} className="w-32" data-testid="backfill-days-input" />
              <span className="text-sm text-slate-500">days back</span>
              <Button onClick={runBackfill} disabled={backfilling} data-testid="run-backfill-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${backfilling ? "animate-spin" : ""}`} /> {backfilling ? "Running…" : "Run backfill"}
              </Button>
              {backfilling && settings.sync_progress && (
                <span className="text-xs text-slate-500 tabular-nums" data-testid="backfill-progress">
                  {settings.sync_progress.total == null
                    ? "Fetching meeting list from Fathom…"
                    : `${settings.sync_progress.done} / ${settings.sync_progress.total} processed`}
                  {settings.sync_progress.errors > 0 && ` · ${settings.sync_progress.errors} errors`}
                </span>
              )}
            </div>
            {settings.last_error && <div className="mt-3 p-2 text-xs text-rose-700 bg-rose-50 rounded border border-rose-200">{settings.last_error}</div>}
          </Card>
        </TabsContent>

        <TabsContent value="mappings" className="mt-4 space-y-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Fathom team roster</div>
              <Button variant="outline" size="sm" onClick={loadRoster} disabled={rosterLoading} data-testid="refresh-roster-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${rosterLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Everyone the Fathom API key can see, live from Fathom, plus anyone whose calls have already synced.
              Unmapped people's calls land with no rep assigned — map them below.
            </p>
            {rosterLoading && !roster ? (
              <p className="text-sm text-slate-400">Loading roster from Fathom…</p>
            ) : roster?.error ? (
              <p className="text-sm text-rose-700">{roster.error}</p>
            ) : (roster?.items || []).length === 0 ? (
              <p className="text-sm text-slate-400">No Fathom users visible to this API key yet.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="fathom-roster-list">
                {roster.items.map(u => (
                  <li key={u.email} className="flex items-center justify-between gap-3 p-2 rounded border border-slate-200">
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-slate-800">{u.name || u.email}</span>
                      {u.name && <span className="ml-2 text-xs text-slate-500">{u.email}</span>}
                      {u.team && <Badge variant="outline" className="ml-2 text-[10px]">{u.team}</Badge>}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 text-xs">
                      <span className="text-slate-500 tabular-nums">{u.call_count} call{u.call_count === 1 ? "" : "s"}</span>
                      {u.mapping_status === "mapped" ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">→ {u.mapped_to}</Badge>
                      ) : u.mapping_status === "auto" ? (
                        <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[10px]">auto → {u.mapped_to}</Badge>
                      ) : (
                        <button
                          type="button"
                          className="text-amber-700 underline decoration-dotted hover:text-amber-900"
                          onClick={() => setNewFathomEmail(u.email)}
                          data-testid={`map-roster-${u.email}`}
                        >
                          unmapped — map now
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">Map Fathom user → HQ sales rep</div>
            <p className="text-sm text-slate-600 mb-3">When a Fathom call comes in with a host email, HQ uses these mappings to assign the right rep. If no mapping exists, HQ also tries to match by the user's HQ email automatically.</p>
            <div className="flex items-center gap-2 mb-4">
              <Input
                placeholder="fathom-user@email.com"
                value={newFathomEmail}
                onChange={(e) => setNewFathomEmail(e.target.value)}
                className="flex-1"
                data-testid="mapping-email-input"
              />
              <Select value={newHqUserId} onValueChange={setNewHqUserId}>
                <SelectTrigger className="w-[260px]" data-testid="mapping-user-select"><SelectValue placeholder="HQ user…" /></SelectTrigger>
                <SelectContent>
                  {reps.map(r => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={createMapping} data-testid="add-mapping-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add</Button>
            </div>
            {mappings.length === 0 ? (
              <p className="text-sm text-slate-400">No mappings yet.</p>
            ) : (
              <ul className="space-y-2">
                {mappings.map(m => (
                  <li key={m.id} className="flex items-center justify-between p-2 rounded border border-slate-200">
                    <div className="text-sm">
                      <span className="text-slate-700 font-medium">{m.fathom_email}</span>
                      <span className="mx-2 text-slate-400">→</span>
                      <span className="text-slate-700">{m.hq_user_name || m.hq_user_email || m.hq_user_id}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteMapping(m.id)} data-testid={`delete-mapping-${m.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-600" /></Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="queue" className="mt-4 space-y-4">
          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">Fix mislinked calls</div>
            <p className="text-sm text-slate-600 mb-3">
              Re-runs the auto-link engine over every call that wasn't linked by hand, using the current matching
              rules. Use after a matching fix to detach calls that landed on the wrong project (e.g. "Solara" calls
              on OLA). Manual links and ignored calls are never touched.
            </p>
            <Button onClick={runRelink} disabled={relinking} data-testid="relink-auto-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${relinking ? "animate-spin" : ""}`} /> {relinking ? "Re-linking…" : "Re-run auto-linking"}
            </Button>
          </Card>
          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">Unlinked calls — admin review</div>
            <p className="text-sm text-slate-600 mb-3">Calls that auto-linking couldn't confidently match to a project. Open each one and link it manually.</p>
            {unlinkedQueue.length === 0 ? (
              <p className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Queue is clean — every call is linked.</p>
            ) : (
              <ul className="space-y-2">
                {unlinkedQueue.map(c => (
                  <li key={c.id}>
                    <a href={`/fathom/calls/${c.id}`} className="block p-3 rounded border border-slate-200 hover:bg-slate-50 transition-colors" data-testid={`queue-call-${c.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800 truncate">{c.meeting_title}</div>
                          <div className="text-xs text-slate-500">
                            {c.meeting_at && new Date(c.meeting_at).toLocaleDateString()} ·
                            {" "}{c.sales_rep_name || <span className="text-amber-600">Unassigned</span>} ·
                            {" "}{c.participant_emails?.length || 0} participants
                          </div>
                        </div>
                        <Badge variant="outline" className={c.link_status === "suggested_match" ? "bg-amber-50 text-amber-700 border-amber-200 text-[10px]" : "text-[10px]"}>{c.link_status}</Badge>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 mb-2">Recent events</div>
            {events.length === 0 ? <p className="text-sm text-slate-500">No events yet.</p> : (
              <ul className="space-y-1 text-xs font-mono">
                {events.map((e, i) => (
                  <li key={e.id || i} className="flex items-center gap-3 py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-400 w-[180px] flex-shrink-0">{e.at && new Date(e.at).toLocaleString()}</span>
                    <Badge variant="outline" className="text-[10px]">{e.event_type}</Badge>
                    {e.recording_id && <span className="text-slate-500 truncate">rec: {e.recording_id}</span>}
                    {e.error && <span className="text-rose-700 truncate">{e.error}</span>}
                    {e.reason && <span className="text-amber-700 truncate">{e.reason}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value, tone, small }) {
  const toneClass = tone === "amber" ? "border-amber-200 bg-amber-50/40" : "border-slate-200";
  return (
    <Card className={`p-4 ${toneClass} min-w-0`}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1.5 truncate">{label}</div>
      <div className={`${small ? "text-sm" : "text-2xl"} font-semibold text-slate-900 tabular-nums truncate`}>{value}</div>
    </Card>
  );
}
